import LeadCaptureBox from "@/components/LeadCaptureBox";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, Zap, Check, ChevronRight, Sparkles, FileText, MousePointer, Eye } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";

const STEPS = [
  {
    n: "01",
    icon: FileText,
    title: "Describe your goal",
    desc: "Type a sentence about your campaign: who it's for, what you're promoting, and the tone you want. The AI handles everything else.",
    detail: "\"Write a re-engagement email for users who haven't opened in 30 days. Friendly, not pushy. Highlight our new automation feature.\"",
  },
  {
    n: "02",
    icon: Sparkles,
    title: "AI generates the full campaign",
    desc: "In seconds you get a subject line, preview text, complete HTML body, and a CTA button — all optimised for opens and clicks.",
    detail: "The model is trained on open-rate data from millions of email sends, so it knows what subject lines actually perform.",
  },
  {
    n: "03",
    icon: Eye,
    title: "Preview and refine",
    desc: "See exactly how the email looks in Gmail desktop, Gmail mobile, and Outlook — including dark mode. Edit inline or regenerate.",
    detail: "No more \"it looked fine in preview\" horror stories. What you see is what your subscribers see.",
  },
  {
    n: "04",
    icon: MousePointer,
    title: "Send or save as template",
    desc: "Schedule to your list or a segment, or save as a reusable template. The AI learns from your campaign's performance for next time.",
    detail: "After sending, the self-optimizing loop kicks in — suggesting improved subject lines based on your actual open data.",
  },
];

const OUTPUTS = [
  { label: "Subject line", desc: "Crafted for curiosity + clarity. Under 50 chars for mobile." },
  { label: "Preview text", desc: "The 90-char snippet that shows in the inbox before opening." },
  { label: "Email body", desc: "Full HTML with sections, bullet points, and brand-appropriate tone." },
  { label: "CTA button", desc: "Action-oriented copy and placement based on campaign goal." },
  { label: "Plain-text fallback", desc: "Auto-generated for email clients that block HTML." },
];

const COMPARE = [
  { them: "Write subject + body yourself", us: "AI generates all copy from a single sentence" },
  { them: "Guess what tone works", us: "Trained on high-converting email patterns" },
  { them: "Separate design + copy steps", us: "Subject, body, CTA, preview text in one shot" },
  { them: "No performance feedback loop", us: "Learns from your open rates to improve future sends" },
];

export default function AiCampaignBuilderPage() {
  return (
    <>
      <SeoHead
        title="AI Campaign Builder — MoreMail | Write Campaigns in Seconds"
        description="MoreMail's AI campaign builder generates subject lines, preview text, full email body, and CTA buttons from a single sentence. Trained on open-rate data from millions of sends."
        url={`${SITE_URL}/ai-campaign-builder`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Features", url: `${SITE_URL}/features` },
          { name: "AI Campaign Builder", url: `${SITE_URL}/ai-campaign-builder` },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-coral/20 via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-white/40 text-xs font-medium mb-8">
            <Link to="/features" className="hover:text-white/70 transition-colors">Features</Link>
            <ChevronRight size={12} />
            <span className="text-white/60">AI Suite</span>
            <ChevronRight size={12} />
            <span className="text-white/80">Campaign Builder</span>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-coral/20 border border-coral/30 rounded-full text-coral text-xs font-bold mb-6">
            <Zap size={11} />
            AI Suite
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5 max-w-3xl">
            Write campaigns in{" "}
            <em className="text-coral not-italic">seconds</em>,<br className="hidden sm:block" />
            not hours.
          </h1>

          <p className="text-white/60 text-base sm:text-lg max-w-2xl mb-10 leading-relaxed">
            Describe your goal in one sentence. MoreMail's AI generates the subject line, preview text, full HTML body, and CTA button — optimised for opens using data from millions of real sends.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <LeadCaptureBox />
            <Link
              to="/features"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/20 text-white/70 font-semibold hover:border-white/40 hover:text-white transition-colors"
            >
              All features
            </Link>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="bg-white border-b border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
            One prompt. Five outputs.
          </h2>
          <p className="text-slate text-sm text-center mb-10">Everything you need to send, generated in one shot.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {OUTPUTS.map((o, i) => (
              <div
                key={o.label}
                className={`bg-paper rounded-2xl border border-black/[0.06] p-5 ${i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <Check size={14} className="text-coral mt-0.5 flex-shrink-0" />
                  <h3 className="font-bold text-ink text-sm">{o.label}</h3>
                </div>
                <p className="text-slate text-xs leading-relaxed pl-[22px]">{o.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">How it works</h2>
        <p className="text-slate text-sm text-center mb-12">Four steps from blank page to ready-to-send.</p>
        <div className="space-y-6">
          {STEPS.map((step) => (
            <div key={step.n} className="flex gap-5 sm:gap-8 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-coral/10 flex items-center justify-center">
                <step.icon size={20} className="text-coral" />
              </div>
              <div className="flex-1 pt-1">
                <div className="flex items-center gap-3 mb-1.5">
                  <span className="text-[10px] font-bold text-slate/50 uppercase tracking-widest">{step.n}</span>
                  <h3 className="font-bold text-ink">{step.title}</h3>
                </div>
                <p className="text-slate text-sm leading-relaxed mb-2">{step.desc}</p>
                {step.detail && (
                  <p className="text-xs text-slate/70 bg-paper border border-black/[0.06] rounded-xl px-4 py-3 italic">
                    {step.detail}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* vs other tools */}
      <section className="bg-white border-y border-black/[0.06]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
            vs. writing it yourself
          </h2>
          <p className="text-slate text-sm text-center mb-10">
            The average marketer spends 3+ hours on a single campaign. MoreMail does the same in under 30 seconds.
          </p>
          <div className="rounded-2xl border border-black/[0.06] overflow-hidden">
            <div className="grid grid-cols-2 bg-paper border-b border-black/[0.06]">
              <div className="px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate">The old way</div>
              <div className="px-5 py-3 text-xs font-bold uppercase tracking-widest text-coral border-l border-black/[0.06]">With MoreMail</div>
            </div>
            {COMPARE.map((row, i) => (
              <div key={i} className={`grid grid-cols-2 ${i % 2 === 0 ? "bg-white" : "bg-paper/50"}`}>
                <div className="px-5 py-4 text-sm text-slate border-b border-black/[0.04] flex items-start gap-2">
                  <span className="text-black/20 mt-0.5 flex-shrink-0">—</span>
                  {row.them}
                </div>
                <div className="px-5 py-4 text-sm text-ink border-b border-black/[0.04] border-l border-black/[0.06] flex items-start gap-2">
                  <Check size={13} className="text-coral mt-0.5 flex-shrink-0" />
                  {row.us}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related features */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <h2 className="font-display text-xl font-bold text-ink mb-6">Works great with</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: "Subject Line Optimisation", href: "/subject-line-optimisation", desc: "A/B test up to 4 AI-generated subject variants and auto-pick the winner." },
            { label: "Inbox Preview", href: "/inbox-preview", desc: "See rendering across Gmail, Outlook, and dark mode before you send." },
            { label: "Spam Score", href: "/spam-score", desc: "Get a 1–10 risk score with specific phrases to fix before sending." },
          ].map((f) => (
            <Link
              key={f.label}
              to={f.href}
              className="bg-paper rounded-2xl border border-black/[0.06] p-5 hover:border-coral/30 hover:shadow-md transition-all group"
            >
              <h3 className="font-bold text-sm text-ink mb-1.5 group-hover:text-coral transition-colors flex items-center justify-between">
                {f.label} <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </h3>
              <p className="text-xs text-slate leading-relaxed">{f.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-coral">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
            Stop staring at a blank page.
          </h2>
          <p className="text-white/80 text-base mb-8">
            Your next campaign is one sentence away. Free to start, no card needed.
          </p>
          <div className="flex justify-center"><LeadCaptureBox /></div>
        </div>
      </section>
    </>
  );
}
